<template>
    <span>
        <el-popover
            v-if="currentConfig.responseType === 'popoverPage' && currentConfig.popoverPageData"
            :placement="popoverPlacement"
            :trigger="popoverTrigger"
            :width="currentConfig.popoverWidth || 400"
            :popper-style="popoverStyle"
            :popper-class="popoverClass"
        >
            <template #reference>
                <span class="text-item-wrap text-item-wrap--interactive" :style="textStyle">
                    {{ getLocaleText(showText) }}
                </span>
            </template>
            <PopoverPageRender
                :area-data="currentConfig.popoverPageData"
                :width="currentConfig.popoverWidth || 400"
                :background-color="currentConfig.popoverBackgroundColor || '#ffffff'"
                :border-radius="getPopoverBorderRadius(currentConfig)"
            />
        </el-popover>

        <component
            v-else
            :is="safeLink ? 'a' : 'div'"
            class="text-item-wrap"
            :target="jumpWay"
            :href="safeLink || undefined"
            :style="textStyle"
            @click="textClickFn"
        >
            {{ getLocaleText(showText) }}
        </component>

        <!-- Popup Dialog -->
        <el-dialog v-if="currentConfig.responseType === 'popup'" :class="currentConfig.popupClass || ''"
            :title="currentConfig.popupTitle || ''" append-to-body v-model="dialogTableVisible">
            <template #default>
                <div v-if="dialogTableVisible" class="new-detail-item-cont" v-html="articleContent.content"></div>
            </template>
            <template #footer>
                <el-button type="primary" @click="dialogTableVisible = false">{{ getLocaleText('关闭') }}</el-button>
            </template>
        </el-dialog>
    </span>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted } from 'vue';
import { getArticleDetails } from "@/api";
import { getSafeLink, isTarget_Blank } from "@/utils/globalData.js";
import { useRenderConfig } from './uses/useRenderConfig.js';
import { resolveBindDataKeyValue } from '@/utils/renderBinding';
import PopoverPageRender from "@/components/render/PopoverPageRender.vue";
import { useLowCodeActionRuntime } from '@/composables/useLowCodeActionRuntime.js';
import { isLowCodePopupPageAction } from '@/utils/lowCodePopupPage.js';


// 获取公共配置的 mixin
import useLocate from './uses/useLocate.js';

const props = defineProps({
    currentElementIndex: {
        type: Array,
        default: () => [],
    },
    idx: {
        type: Number,
        default: -1,
    },
    parentKeys: {
        type: Array,
        default: () => [],
    },
    disabled: {
        type: Boolean,
        default: true,
    },
    config: {
        type: Object,
    },
});
const { dispatchLowCodeAction } = useLowCodeActionRuntime();
const getConfigJson = inject('getConfigJson', () => ({}));

const dialogTableVisible = ref(false);
const articleContent = ref('');


let {
    langJson,
    nowLocale,
    getLocaleText,
} = useLocate()
const { bindData, filterConfig } = useRenderConfig(() => props.config);
const currentConfig = computed(() => filterConfig.value || props.config || {});
const currentClickAction = computed(() => {
    if (isLowCodePopupPageAction(currentConfig.value)) return currentConfig.value;
    return currentConfig.value?.clickAction;
});

const showText = computed(() => {
    return resolveBindDataKeyValue(
        bindData.value,
        currentConfig.value?.bindDataKey,
        currentConfig.value?.text || "",
    );
});

const textStyle = computed(() => {
    return {
        textAlign: currentConfig.value?.textAlign,
        fontSize: `${currentConfig.value?.fontSize || 14}px`,
        fontWeight: currentConfig.value?.fontWeight,
        fontStyle: currentConfig.value?.italic ? 'italic' : 'normal',
        fontFamily: currentConfig.value?.fontFamily || undefined,
        textDecoration: currentConfig.value?.underline ? 'underline' : 'none',
        color: currentConfig.value?.fontColor,
        lineHeight: currentConfig.value?.lineHeight,
        whiteSpace: currentConfig.value?.allowWrap === false ? 'nowrap' : 'pre-wrap',
        overflowWrap: currentConfig.value?.allowWrap === false ? 'normal' : 'break-word',
    };
});

const popoverPlacement = computed(() => {
    return currentConfig.value?.popoverPlacement || "right";
});

const popoverTrigger = computed(() => {
    return currentConfig.value?.popoverTrigger || "hover";
});

const popoverClass = computed(() => {
    return ["text-item-popover", currentConfig.value?.popupClass || ""].filter(Boolean).join(" ");
});

const popoverStyle = computed(() => {
    return {
        padding: "0px",
        border:'none',
        overflow: "hidden",
        borderRadius: `${getPopoverBorderRadius(currentConfig.value)}px`,
        backgroundColor: currentConfig.value?.popoverBackgroundColor || "#ffffff",
    };
});

function getPopoverBorderRadius(config) {
    return typeof config?.popoverBorderRadius === "number"
        ? config.popoverBorderRadius
        : 6;
}

const safeLink = computed(() => {
    if (!["link", "jumpLink"].includes(currentConfig.value?.responseType || "link")) {
        return "";
    }
    return getSafeLink(currentConfig.value?.link);
});
// 获取文章内容
const getArticleContent = async () => {
    if (currentConfig.value?.responseType !== "popup" || !currentConfig.value?.article?.id) {
        return;
    }
    const param = {
        pageNo: 0,
        pageSize: 9999,
        id: currentConfig.value.article.id,
        status: "1",
    };
    const res = await getArticleDetails(param);
    if (res.data.dataList) {
        articleContent.value = res.data.dataList[0];
        if (articleContent.value.type === 1 && articleContent.value.content) {
            articleContent.value.content = articleContent.value.content.replace('video', 'video autoplay ');
        }
    }
};

// 响应式的计算
const currentElementIndexStr = computed(() => {
    return props.currentElementIndex.length ? props.currentElementIndex.join("x") : "";
});

// 处理跳转方式
const jumpWay = computed(() => {
    switch (currentConfig.value?.responseType) {
        case 'link':
            return '_Self';
        case 'jumpLink':
            return isTarget_Blank();
        default:
            return '_Self';
    }
});

// 监听配置的变化
watch(() => currentConfig.value?.article, (value) => {
    if (value) getArticleContent();
    else articleContent.value = '';
}, { deep: true, immediate: true });

watch(() => currentConfig.value, () => {
    // 其他配置变化时的逻辑
}, { deep: true, immediate: true });

// 初始化时获取文章内容
onMounted(() => {
    getArticleContent();
});

// 方法
const textClickFn = (event) => {
    if (isLowCodePopupPageAction(currentClickAction.value)) {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        dispatchLowCodeAction(
            currentClickAction.value,
            typeof getConfigJson === 'function' ? getConfigJson() : {},
        );
        return;
    }
    if (currentConfig.value?.responseType !== 'popup') {
        return;
    }
    dialogTableVisible.value = true;
};

</script>

<style scoped>
.text-item-wrap {
    display: block;
    text-decoration: none;
}
.text-item-wrap--interactive {
    cursor: pointer;
}
</style>

<style>
.text-item-popover.el-popover {
    padding: 0 !important;
}
</style>
